import { SelectButton } from '@modules/queryBuilder/components/SelectButton';
import {
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
  USER_SINGLE_VALUE_INPUT_OPERATORS,
} from '@modules/queryBuilder/constants';
import {
  getFieldOperators,
  getFilterValueFromType,
  getFilterValueType,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { ButtonProps, MenuItem, MenuList } from '@mui/material';
import { OperatorContainsAnyOf } from 'v2/core/services/queryBuilder/operators';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';

interface QueryFilterOperatorSelectButtonProps {
  filter: NormalisedFilter;
  buttonColor: ButtonProps['color'];
  readOnly: boolean;
}

export function QueryFilterOperatorSelectButton({
  filter,
  buttonColor,
  readOnly,
}: QueryFilterOperatorSelectButtonProps) {
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );

  const eqlField = eqlFieldByFieldID[filter.field]?.field;

  if (!eqlField) {
    return null;
  }

  const filterValueType = getFilterValueType(eqlField);
  const operators = getFieldOperators({
    field: filter.field,
    filterValueType: filterValueType,
  });

  const getTypeForOperator = (newOperator: string) => {
    if (NO_VALUE_OPERATORS.includes(newOperator)) {
      return 'boolean';
    }
    if (newOperator === OperatorContainsAnyOf) {
      return 'string[]';
    }
    return filterValueType;
  };

  const onOperatorChange = (newOperator: string) => {
    if (filter.operator === newOperator) {
      return;
    }

    const isSameOperatorType = [
      MULTI_VALUE_OPERATORS,
      NO_VALUE_OPERATORS,
      USER_SINGLE_VALUE_INPUT_OPERATORS,
    ].some(
      (operatorTypeGroup) =>
        operatorTypeGroup.includes(filter.operator) &&
        operatorTypeGroup.includes(newOperator),
    );

    const shouldResetValue =
      filter.operator === OperatorContainsAnyOf ||
      newOperator === OperatorContainsAnyOf;

    let value = filter.value;

    if (!isSameOperatorType || shouldResetValue) {
      const type = getTypeForOperator(newOperator);
      value = getFilterValueFromType({ type });
    }

    const updatedNodes = upsertQueryFilterNode({
      node: { ...filter, value, operator: newOperator },
      nodesByID: nodes,
      parentID: filter.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
  };

  return (
    <SelectButton
      className="qb-filter-operator-select-button"
      shouldShowTooltip={false}
      label={filter.operator ?? ''}
      buttonColor={buttonColor}
      readOnly={readOnly}
      renderDropDownComponent={({ onClose }) => (
        <MenuList
          id={`qb-filter-operator-dropdown-${filter.id}`}
          dense
          autoFocusItem
          onKeyDown={(event) => {
            if (event.key === 'Tab') {
              event.preventDefault();
              onClose();
            }
          }}
        >
          {operators.map((operator) => {
            return (
              <MenuItem
                key={operator}
                value={operator}
                autoFocus={operator === filter.operator}
                selected={operator === filter.operator}
                onClick={() => {
                  onOperatorChange(operator);
                  onClose();
                }}
              >
                {operator}
              </MenuItem>
            );
          })}
        </MenuList>
      )}
    />
  );
}
